import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../../stores/auth.store';
import { toast } from '../../../stores/toast.store';
import { confirm as confirmDialog } from '../../../stores/confirm.store';
import { api } from '../../../lib/api';
import { Icon } from '../../../components/ui/Icon';
import { Chip } from '../../../components/ui/Chip';
import { Helper } from '../../../components/ui/Helper';
import { EmptyState } from '../../../components/ui/EmptyState';
import { SkeletonTable } from '../../../components/ui/Skeleton';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { MobileFilters } from '../../../components/ui/MobileFilters';
import {
  OrdinaFiltra, perData, perNumero, perTesto, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina,
} from '../../../components/ui/OrdinaFiltra';
import { NewRecurringModal } from '../../../components/modals/NewRecurringModal';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../../lib/utils';
import { canWriteAdmin, isoDay, isoDayPlus, plurale, type PayMethod } from '../adminApi';
import { useAdminActions } from '../useAdminActions';
import {
  CATEGORIA_SPESA_LABEL, METODO_LABEL, OGNI_QUANTO, dataEsatta, giornoInParole, ilGiorno, alGiorno, dalGiorno, delGiorno, nomeBreve, quando, speseFisseQuery,
  type AutoARate, type SpesaFissa, type SpeseFisseData,
} from '../fatture/fattureApi';

/**
 * Spese › Spese fisse (proposta § 2.3): le spese ricorrenti che prima nessuna
 * schermata elencava.
 *
 *   riga di testa  "N spese fisse · X al mese" (solo le voci in vigore)
 *   gruppi         per categoria, con il totale al mese del gruppo
 *   voce           nome, quanto e ogni quanto, giorno, prossimo addebito, come
 *                  si paga; Modifica la spesa · Sospendi · Termina
 *   Auto pagate a rate   il valore finanziato e la rata al mese, con "Rate
 *                  dell'auto che mancano › Aggiungi" (spostato qui da Banca)
 *   in fondo       le spese fisse sospese o terminate, con Riattiva
 *
 * Senza stipendi, TFR e compenso dell'amministratore (vivono in Dipendenti): li
 * toglie l'API, /admin/spese/fisse.
 *
 * Cerca, ordina e filtra (24/9, richiesta di Lorenzo): tutto nel browser, la
 * lista arriva intera. "Divise per categoria" è la vista di sempre, con i
 * totali al mese dei gruppi; con qualunque altro ordine i gruppi spariscono e
 * resta una lista unica (i totali per categoria vivono solo nei gruppi). Il
 * confronto sul costo è sempre AL MESE: un abbonamento annuale da € 120 pesa
 * come uno mensile da € 10, non come uno da € 120.
 */

const ORDINI: OpzioneOrdina<SpesaFissa>[] = [
  { key: 'categoria', label: 'Divise per categoria' },
  { key: 'prossimo', label: 'Prossimo addebito prima', criteri: [perData((v) => v.nextDueDate), perTesto((v) => v.name)] },
  { key: 'costose', label: 'Prima le più costose al mese', criteri: [perNumero((v) => v.alMese, 'decrescente'), perTesto((v) => v.name)] },
  { key: 'economiche', label: 'Prima le meno costose al mese', criteri: [perNumero((v) => v.alMese), perTesto((v) => v.name)] },
  { key: 'nome', label: 'Nome dalla A alla Z', criteri: [perTesto((v) => v.name)] },
];

/** Le spese fisse che si possono scegliere nel filtro "Quali spese fisse". */
function statoSpesa(v: SpesaFissa, oggi: string, fra7: string): string[] {
  const s: string[] = [];
  if (v.inVigore && v.nextDueDate && v.nextDueDate < oggi) s.push('scadute');
  if (v.inVigore && v.nextDueDate && v.nextDueDate >= oggi && v.nextDueDate <= fra7) s.push('entro7');
  if (!v.inVigore && !v.endDate) s.push('sospese');
  if (!v.inVigore && v.endDate) s.push('terminate');
  return s;
}

const RECURRING_MONTHS: Record<SpesaFissa['cycle'], number> = { MONTHLY: 1, BIMONTHLY: 2, QUARTERLY: 3, YEARLY: 12 };

/** La prima data >= oggi della ricorrenza: serve quando una voce sospesa torna attiva. */
function prossimaDaOggi(r: SpesaFissa): string {
  const oggi = isoDay();
  const base = r.nextDueDate ?? r.startDate ?? oggi;
  const [y, m] = base.split('-').map(Number);
  const giorno = r.dayOfMonth ?? Number(base.slice(8, 10));
  let yy = y!;
  let mm = m! - 1;
  for (let i = 0; i < 240; i++) {
    const last = new Date(Date.UTC(yy, mm + 1, 0)).getUTCDate();
    const d = new Date(Date.UTC(yy, mm, Math.min(giorno, last))).toISOString().slice(0, 10);
    if (d >= oggi) return d;
    mm += RECURRING_MONTHS[r.cycle];
    while (mm > 11) { mm -= 12; yy++; }
  }
  return oggi;
}

/** "il 25 di ogni mese", "il 14, ogni due mesi", "il 25 marzo". */
export function giornoDellaSpesa(r: SpesaFissa): string {
  if (!r.dayOfMonth) return '';
  if (r.cycle === 'YEARLY' && r.nextDueDate) return `${ilGiorno(r.nextDueDate).replace(/\s\d{4}$/, '')}`;
  if (r.cycle === 'MONTHLY') return `il ${r.dayOfMonth} di ogni mese`;
  return `il ${r.dayOfMonth}, ${OGNI_QUANTO[r.cycle]}`;
}

/** Esportata anche per SpeseMobile: sul telefono gli stessi gesti stanno nel pannello della riga. */
export function useAzioniSpesaFissa() {
  const qc = useQueryClient();
  const invalida = () => qc.invalidateQueries({ queryKey: ['admin'] });
  const patch = (id: string, data: Record<string, unknown>) => api.patch(`/admin/recurring/${id}`, data);

  const sospendi = useMutation({
    mutationFn: (r: SpesaFissa) => patch(r.id, { active: false }),
    onSuccess: (_x, r) => {
      invalida();
      toast.undo(`${r.name}: spesa fissa sospesa`, {
        description: 'Non compare più fra le spese da pagare finché non la riattivi.',
        onUndo: () => { patch(r.id, { active: true }).then(invalida).catch((e: any) => toast.alert('Spesa fissa non riattivata', e?.message ?? 'Riprova')); },
      });
    },
    onError: (e: any) => toast.alert('Spesa fissa non sospesa', e?.message ?? 'Riprova'),
  });

  const termina = useMutation({
    mutationFn: async (r: SpesaFissa) => {
      const ok = await confirmDialog({
        title: `Terminare la spesa fissa ${r.name}?`,
        body: "Da oggi non la paghiamo più: esce dalle spese da pagare e dalla previsione. Le volte che è già stata pagata restano nello storico.",
        confirmLabel: 'Termina la spesa fissa',
        icon: 'x',
      });
      if (!ok) return null;
      return patch(r.id, { active: false, endDate: isoDay() });
    },
    onSuccess: (x, r) => { if (x === null) return; invalida(); toast.ok(`${r.name}: spesa fissa terminata`); },
    onError: (e: any) => toast.alert('Spesa fissa non terminata', e?.message ?? 'Riprova'),
  });

  const riattiva = useMutation({
    mutationFn: (r: SpesaFissa) => patch(r.id, { active: true, endDate: null, nextDueDate: prossimaDaOggi(r) }),
    onSuccess: (_x, r) => { invalida(); toast.ok(`${r.name}: spesa fissa di nuovo attiva`, `Prossimo addebito ${giornoInParole(prossimaDaOggi(r))}.`); },
    onError: (e: any) => toast.alert('Spesa fissa non riattivata', e?.message ?? 'Riprova'),
  });

  return { sospendi, termina, riattiva };
}

export function SpeseFisse({ tabs }: { tabs: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteAdmin(user);
  const q = useQuery(speseFisseQuery);
  const [modifica, setModifica] = useState<SpesaFissa | null>(null);
  const data = q.data;

  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const ordina = useOrdina(ORDINI);
  const perCategoria = ordina.key === 'categoria';
  const filtriDefs = useMemo<FiltroDef<SpesaFissa>[]>(() => {
    const items = data?.items ?? [];
    const oggi = isoDay();
    const fra7 = isoDayPlus(7);
    const categorie = [...new Set(items.map((v) => v.category))]
      .sort((a, b) => (CATEGORIA_SPESA_LABEL[a] ?? a).localeCompare(CATEGORIA_SPESA_LABEL[b] ?? b, 'it'));
    const metodi = [...new Set(items.map((v) => v.method ?? ''))]
      .sort((a, b) => (a ? METODO_LABEL[a as PayMethod] : 'zzz').localeCompare(b ? METODO_LABEL[b as PayMethod] : 'zzz', 'it'));
    return [
      {
        param: 'solo', label: 'Quali spese fisse',
        opzioni: [
          { value: 'scadute', label: 'Solo le spese scadute' },
          { value: 'entro7', label: 'Solo quelle da pagare nei prossimi 7 giorni' },
          { value: 'sospese', label: 'Solo le spese sospese' },
          { value: 'terminate', label: 'Solo le spese terminate' },
        ],
        test: (v, x) => statoSpesa(v, oggi, fra7).includes(x),
      },
      {
        param: 'categoria', label: 'Categoria della spesa', multi: true,
        opzioni: categorie.map((c) => ({ value: c, label: CATEGORIA_SPESA_LABEL[c] ?? c })),
        test: (v, x) => v.category === x,
      },
      {
        param: 'metodo', label: 'Come si paga', multi: true,
        opzioni: metodi.map((m) => ({ value: m || 'nessuno', label: m ? METODO_LABEL[m as PayMethod] : 'Metodo non indicato' })),
        test: (v, x) => (v.method ?? 'nessuno') === x,
      },
    ];
  }, [data]);
  const filtri = useFiltri(filtriDefs, data?.items);

  const s = search.trim().toLowerCase();
  const scelte = useMemo(() => {
    const cercate = (data?.items ?? []).filter((v) => !s || `${v.name} ${v.counterparty ?? ''} ${v.note ?? ''}`.toLowerCase().includes(s));
    return ordina.applica(filtri.applica(cercate));
  }, [data, s, ordina, filtri]);
  const inVigore = useMemo(() => scelte.filter((v) => v.inVigore && !v.auto), [scelte]);
  const ferme = useMemo(() => scelte.filter((v) => !v.inVigore && !v.auto), [scelte]);
  const ristretta = !!s || filtri.attivi > 0;
  // Le auto a rate seguono i filtri tramite la loro spesa fissa (la rata).
  const auto = useMemo(() => {
    if (!data) return [];
    if (!ristretta) return data.auto;
    const ids = new Set(scelte.map((v) => v.id));
    return data.auto.filter((a) => a.recurring && ids.has(a.recurring.id));
  }, [data, scelte, ristretta]);

  if (q.isLoading && !data) {
    return <>{tabs}<div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={8} cols={5} withAvatar={false} /></tbody></table></div></>;
  }
  if (!data) return <>{tabs}<EmptyState brand title="Le spese fisse non si sono caricate" description="Riprova tra qualche secondo." /></>;
  if (data.items.length === 0 && data.auto.length === 0) {
    return <>{tabs}<EmptyState brand title="Nessuna spesa fissa" description="Affitto, utenze, abbonamenti e rate registrati come spese fisse compaiono qui." /></>;
  }

  return (
    <>
      <p className="fs-frase">{frase(data)}</p>

      <div className="fs-testa">
        <span>{data.totals.count === 1 ? '1 spesa fissa' : `${data.totals.count} spese fisse`}</span>
        <span className="mute">·</span>
        <span><span className="mono">€ {fmtEur(data.totals.monthly, true)}</span> al mese</span>
        <Helper text="Le spese fisse in vigore oggi, auto a rate comprese. Quelle che si pagano ogni due mesi, ogni tre o una volta all'anno pesano per la loro parte di un mese. Stipendi, TFR e compenso dell'amministratore sono in Dipendenti." />
      </div>

      {tabs}

      <MobileFilters attivi={filtri.attivi + (s ? 1 : 0)} label="Cerca, ordina e filtra">
        <div className="filterbar filterbar--float mb-4 flex-wrap">
          <div className="tb-search flex-1" style={{ minWidth: 200, margin: 0 }}>
            <Icon name="search" size={12} />
            <input aria-label="Cerca fra le spese fisse" placeholder="Cerca per nome della spesa o fornitore…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {ristretta && (
            <span className="mute text-[12px]">{plurale(inVigore.length + ferme.length + auto.length, 'spesa fissa', 'spese fisse')} in lista</span>
          )}
          <OrdinaFiltra ordina={ordina} filtri={filtri} />
        </div>
      </MobileFilters>

      {ristretta && inVigore.length === 0 && ferme.length === 0 && auto.length === 0 && (
        <EmptyState brand title="Nessuna spesa fissa con questi filtri" description="Togli un filtro o cambia la ricerca."
          action={<div className="flex gap-2 justify-center">
            {filtri.attivi > 0 && <button className="btn" onClick={filtri.clear}>Togli i filtri</button>}
            {s && <button className="btn" onClick={() => setSearch('')}>Azzera la ricerca</button>}
          </div>} />
      )}

      {perCategoria ? data.groups.map((g) => {
        const voci = inVigore.filter((v) => v.category === g.category);
        if (!voci.length) return null;
        // Il totale del gruppo è quello delle righe che si vedono: senza filtri
        // coincide con quello dell'API (solo le spese della società).
        const alMese = voci.filter((v) => v.entity === 'MASAMUNE').reduce((t, v) => t + v.alMese, 0);
        return (
          <div key={g.category} className="ed-section fs-group">
            <div className="data-hd">
              <span className="t-micro">{CATEGORIA_SPESA_LABEL[g.category] ?? g.category}</span>
              <span className="mono mute text-[12px]">€ {fmtEur(ristretta ? alMese : g.monthly, true)} al mese</span>
            </div>
            <TabellaSpeseFisse voci={voci} canWrite={canWrite} onModifica={setModifica} />
          </div>
        );
      }) : inVigore.length > 0 && (
        <div className="ed-section fs-group">
          <div className="data-hd">
            <span className="t-micro">Spese fisse in vigore · {ordina.attiva.label.toLowerCase()}</span>
            <span className="mono mute text-[12px]">{plurale(inVigore.length, 'spesa', 'spese')}</span>
          </div>
          <TabellaSpeseFisse voci={inVigore} canWrite={canWrite} onModifica={setModifica} conCategoria
            conAlMese={ordina.key === 'costose' || ordina.key === 'economiche'} />
        </div>
      )}

      {auto.length > 0 && <AutoPagateARate data={data} auto={auto} canWrite={canWrite} onModifica={(id) => setModifica(data.items.find((v) => v.id === id) ?? null)} />}

      {ferme.length > 0 && (
        <div className="ed-section fs-group adm-fold">
          <div className="data-hd">
            <span className="t-micro">Spese fisse sospese o terminate</span>
          </div>
          <TabellaSpeseFisse voci={ferme} canWrite={canWrite} onModifica={setModifica} ferme conCategoria={!perCategoria} />
        </div>
      )}

      {modifica && createPortal(
        <NewRecurringModal recurring={modifica} onClose={() => setModifica(null)} />,
        document.body,
      )}
    </>
  );
}

function frase(d: SpeseFisseData): ReactNode {
  const oggi = isoDay();
  const prossima = d.items
    .filter((v) => v.inVigore && v.nextDueDate && v.nextDueDate >= oggi)
    .sort((a, b) => (a.nextDueDate ?? '').localeCompare(b.nextDueDate ?? ''))[0];
  const arretrate = d.items.filter((v) => v.inVigore && v.nextDueDate && v.nextDueDate < oggi).length;
  const parti: ReactNode[] = [];
  parti.push(prossima
    ? <>Con i dati di oggi il prossimo addebito fisso è {nomeBreve(prossima.name)}, {quando(prossima.nextDueDate)}.</>
    : <>Con i dati di oggi non ci sono addebiti fissi in arrivo.</>);
  if (arretrate > 0) {
    parti.push(<> {arretrate === 1 ? "Una spesa fissa ha un addebito passato che non è ancora segnato pagato" : `${arretrate} spese fisse hanno un addebito passato che non è ancora segnato pagato`}: si conferma da Spese da pagare.</>);
  }
  if (d.rateMancanti.some((m) => m.addebiti > 0)) {
    parti.push(<> In banca ci sono rate delle auto che qui non risultano: le trovi in fondo, in <strong>Auto pagate a rate</strong>.</>);
  }
  const ferme = d.totals.ferme;
  if (ferme > 0) parti.push(<> {ferme === 1 ? 'Una spesa fissa è sospesa o terminata' : `${ferme} spese fisse sono sospese o terminate`}: sono in fondo alla pagina.</>);
  return <>{parti.map((p, i) => <Fragment key={i}>{p}</Fragment>)}</>;
}

function TabellaSpeseFisse({ voci, canWrite, onModifica, ferme, conCategoria, conAlMese }: {
  voci: SpesaFissa[]; canWrite: boolean; onModifica: (v: SpesaFissa) => void; ferme?: boolean;
  /** Lista unica (non divisa per categoria): la categoria si scrive sotto il nome. */
  conCategoria?: boolean;
  /** Ordinata per costo: sotto l'importo, quanto pesa al mese (per le non mensili). */
  conAlMese?: boolean;
}) {
  return (
    <table className="tbl tbl--ed tbl--card tbl--static">
      <thead>
        <tr>
          <th>Spesa fissa</th>
          <th className="num" style={{ width: 190 }}>Quanto costa</th>
          <th style={{ width: 190 }}>Quando si paga</th>
          <th style={{ width: 190 }}>{ferme ? 'Stato della spesa' : 'Prossimo addebito'}</th>
          <th style={{ width: 200 }}>Come si paga</th>
          <th style={{ width: 150 }} />
        </tr>
      </thead>
      <tbody>
        {voci.map((v) => <RigaSpesaFissa key={v.id} v={v} canWrite={canWrite} onModifica={onModifica} ferma={!!ferme} conCategoria={conCategoria} conAlMese={conAlMese} />)}
      </tbody>
    </table>
  );
}

function RigaSpesaFissa({ v, canWrite, onModifica, ferma, conCategoria, conAlMese }: {
  v: SpesaFissa; canWrite: boolean; onModifica: (v: SpesaFissa) => void; ferma: boolean; conCategoria?: boolean; conAlMese?: boolean;
}) {
  const sotto = [
    v.counterparty && v.counterparty !== v.name ? v.counterparty : null,
    conCategoria ? (CATEGORIA_SPESA_LABEL[v.category] ?? v.category) : null,
  ].filter(Boolean).join(' · ');
  const { sospendi, termina, riattiva } = useAzioniSpesaFissa();
  const menu: ActionItem[] = ferma
    ? [{ key: 'riattiva', label: 'Riattiva la spesa fissa', icon: 'repeat', onClick: () => riattiva.mutate(v), hidden: !canWrite }]
    : [
      { key: 'sospendi', label: 'Sospendi la spesa fissa per ora', icon: 'clock', onClick: () => sospendi.mutate(v), hidden: !canWrite },
      { key: 'termina', label: 'Termina: non la paghiamo più', icon: 'x', danger: true, onClick: () => termina.mutate(v), hidden: !canWrite },
    ];
  return (
    <tr style={{ cursor: 'default' }}>
      <td data-m="hd">
        <div className="font-semibold flex items-center gap-2">
          {v.name}
          {v.entity === 'PERSONALE_ANDREA' && <Chip tone="ghost">Di Andrea</Chip>}
        </div>
        {sotto && <div className="text-[12px] mute">{sotto}</div>}
      </td>
      <td data-m="key" data-l="Quanto costa" className="num" style={{ whiteSpace: 'nowrap' }}>
        <span className="mono font-semibold">€ {fmtEur(v.amountGross, true)}</span> <span className="mute text-[12px]">{OGNI_QUANTO[v.cycle]}</span>
        {conAlMese && v.cycle !== 'MONTHLY' && (
          <div className="mute text-[12px]" title="Il costo diviso per i mesi che copre: è il numero su cui si ordina">
            cioè <span className="mono">€ {fmtEur(v.alMese, true)}</span> al mese
          </div>
        )}
      </td>
      <td data-m="key" data-l="Quando si paga" className="mute">{giornoDellaSpesa(v)}</td>
      <td data-m="key" data-l={ferma ? 'Stato della spesa' : 'Prossimo addebito'} title={ferma ? undefined : dataEsatta(v.nextDueDate, 'Prossimo addebito')}>
        {ferma
          ? <Chip tone="ghost" dot>{v.endDate ? `Terminata il ${fmtDate(v.endDate)}` : 'Sospesa'}</Chip>
          : <span className={(v.nextDueDate ?? '') < isoDay() ? 'text-alert' : 'mute'}>{quando(v.nextDueDate, 'scaduta')}</span>}
      </td>
      <td data-m="off" className="mute" style={{ whiteSpace: 'nowrap' }}>{v.method ? METODO_LABEL[v.method] : '—'}</td>
      <td data-m="row" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end items-center gap-1">
          {canWrite && (
            <button className="btn btn-sm btn-ghost" onClick={() => onModifica(v)}>
              <Icon name="pencil" size={10} /> Modifica la spesa
            </button>
          )}
          <ActionsMenu items={menu} label={`Azioni su ${v.name}`} />
        </div>
      </td>
    </tr>
  );
}

// ════════════════════════════════════════════════════════════════════
// Auto pagate a rate
// ════════════════════════════════════════════════════════════════════

/**
 * Le auto: la finanziaria ha già pagato il fornitore, noi paghiamo la rata ogni
 * mese. Il valore dell'auto sta solo qui (era "da ammortizzare" in Fornitori);
 * la rata al mese è una spesa fissa come le altre, ma si vede in questo blocco
 * e non nei gruppi di sopra, per non contarla due volte a schermo.
 */
function AutoPagateARate({ data, auto, canWrite, onModifica }: { data: SpeseFisseData; auto: AutoARate[]; canWrite: boolean; onModifica: (recurringId: string) => void }) {
  const { creaRateMancanti } = useAdminActions();
  const mancanti = data.rateMancanti.filter((m) => m.addebiti > 0);
  return (
    <div className="ed-section fs-group">
      <div className="data-hd">
        <span className="t-micro">Auto pagate a rate</span>
        <span className="mono mute text-[12px]">€ {fmtEur(data.totals.autoMonthly, true)} al mese</span>
      </div>
      <div className="adm-note">
        <Icon name="help" size={12} className="mute" />
        <span>Il fornitore dell'auto è già stato pagato dalla finanziaria: noi paghiamo la rata ogni mese. Per questo il valore dell'auto non è fra le spese da pagare.</span>
      </div>
      <table className="tbl tbl--ed tbl--card tbl--static">
        <thead>
          <tr>
            <th>Auto</th>
            <th>Chi la finanzia</th>
            <th className="num" style={{ width: 170 }}>Valore finanziato</th>
            <th className="num" style={{ width: 150 }}>Rata al mese</th>
            <th style={{ width: 170 }}>Prossima rata</th>
            <th style={{ width: 150 }} />
          </tr>
        </thead>
        <tbody>
          {auto.map((a) => <RigaAuto key={a.payableId} a={a} canWrite={canWrite} onModifica={onModifica} />)}
        </tbody>
      </table>

      {mancanti.length > 0 && (
        <div className="mt-4">
          <div className="t-micro mb-2">Rate dell'auto che mancano</div>
          {mancanti.map((m) => (
            <div key={m.recurringId} className="adm-line">
              <div className="min-w-0 flex-1">
                In banca le rate di <strong>{m.nome}</strong> partono {m.dallaBanca ? `${dalGiorno(m.dallaBanca)}` : 'da prima'},
                qui {m.dalGestionale ? `${dalGiorno(m.dalGestionale)}` : 'mancano'}: {m.addebiti === 1 ? 'manca 1 rata' : `mancano ${m.addebiti} rate`}.
              </div>
              <div className="mono font-semibold text-right" style={{ width: 110 }}>€ {fmtEur(m.totaleAddebiti, true)}</div>
              <div style={{ width: 128 }} className="flex justify-end">
                {canWrite && (
                  <button className="btn btn-sm" disabled={creaRateMancanti.isPending}
                    onClick={() => creaRateMancanti.mutate({ recurringId: m.recurringId, nome: m.nome, addebiti: m.addebiti, totale: m.totaleAddebiti })}>
                    <Icon name="plus" size={10} /> Aggiungi le rate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RigaAuto({ a, canWrite, onModifica }: { a: AutoARate; canWrite: boolean; onModifica: (recurringId: string) => void }) {
  const rec = a.recurring;
  return (
    <tr style={{ cursor: 'default' }}>
      <td data-m="hd">
        <div className="font-semibold">{a.vendorName}</div>
        <div className="text-[12px] mute">Fattura n. <span className="mono">{a.number ?? 's/n'}</span>{a.issueDate ? ` del ${fmtDate(a.issueDate)}` : ''}</div>
      </td>
      <td data-m="key" data-l="Chi la finanzia">{rec ? (rec.counterparty ?? rec.name) : <span className="mute">rata non collegata</span>}</td>
      <td data-m="key" data-l="Valore finanziato" className="num mono">€ {fmtEur(a.financed)}</td>
      <td data-m="key" data-l="Rata al mese" className="num mono font-semibold">{rec ? `€ ${fmtEur(rec.monthly, true)}` : '—'}</td>
      <td data-m="key" data-l="Prossima rata" title={dataEsatta(rec?.nextDueDate, 'Prossima rata')} className="mute">
        {rec ? (rec.inVigore ? quando(rec.nextDueDate, 'scaduta') : 'rata ferma') : '—'}
      </td>
      <td data-m="row">
        <div className="flex justify-end">
          {canWrite && rec && (
            <button className="btn btn-sm btn-ghost" onClick={() => onModifica(rec.id)}>
              <Icon name="pencil" size={10} /> Modifica la rata
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
